<template>
    <section
        class="tw:mb-8"
        :class="deshabilitado ? 'tw:opacity-60 tw:pointer-events-none' : ''"
    >
        <!-- Error de flujo (p.ej. solicitarCobro falló): bloquea el avance. -->
        <b-alert v-if="error" variant="danger" show class="tw:mt-2 tw:whitespace-pre-line">
            {{ error }}
        </b-alert>

        <!-- Cupón manual: modo CONTROLADO — el composable es la única fuente
             de verdad. El hijo solo emite `verificar`/`quitar` y el estado
             visual (aplicado/valido/error) llega por props confirmado por el
             backend (guardar-descuentos). Si el guardado o solicitarCobro
             falla, el padre no cambia las props y la UI no muestra éxito. -->
        <CuponDescuentoComponent
            v-if="cartId"
            :cart_id="cartId"
            :loading="loading || applying"
            :aplicado="!!cuponVerificado"
            :valido="!!cuponVerificado"
            :mensaje-externo="error"
            @verificar="(codigo) => $emit('verificar-cupon', codigo)"
            @quitar="() => $emit('quitar-cupon')"
        />

        <!-- Desglose de precio con/sin gastos de gestión + descuentos.
             Mismo patrón que ResumenCompraComponent (abonos/eventos SGA):
             `ul.resumen d-md-flex` + `li.altcolor-content` + total con
             `price price-large gradient-color`. -->
        <div class="mt-3 mb-4">
            <h2 class="m-0 h1 text-uppercase">{{ t('simpatizantes.resumen_compra') }}</h2>
            <div class="subtitle h3 light mb-3 mb-lg-4">{{ t('simpatizantes.comprueba_info') }}</div>

            <div class="mt-3 col-md-8 col-12 px-0">
                <ul class="resumen d-md-flex">
                    <li class="altcolor-content">
                        <div class="text-bold oswald text-uppercase m-0 w-100">
                            1. {{ precioInfo.amount | formatear_precio }}
                        </div>
                    </li>
                    <li>
                        <div class="h5 text-bold oswald text-uppercase m-0 w-100">
                            {{ precioInfo.salesCategoryName }}
                        </div>
                    </li>
                    <li class="text-bold pr-0 pl-0">
                        <label class="h5 pr-0">{{ t('simpatizantes.precio') }}</label>
                        <div class="h5 m-lg-0">
                            <template v-if="descuentos.length > 0">
                                <span>{{ importeConDescuento | formatear_precio }}</span>
                                <span>
                                    <small>(<del>{{ precioInfo.amount | formatear_precio }}</del>)</small>
                                </span>
                            </template>
                            <template v-else>
                                {{ importeConDescuento | formatear_precio }}
                            </template>
                        </div>
                        <div
                            v-for="(d, i) in descuentos"
                            :key="i"
                            class="text-small text-success pt-1"
                        >
                            {{ d.name }} (-{{ formatDescuento(d) }})
                        </div>
                    </li>
                </ul>
                <div class="mb-5">
                    <label>{{ t('simpatizantes.total') }}</label>
                    <div class="price price-large gradient-color">{{ totalPagar | formatear_precio }}</div>
                </div>
            </div>
        </div>

        <!-- FormTerminos: el wrapper ya emite `continuar(continuar, privacidad, condiciones, comunicaciones, plazos, renovacion)`.
             margin de fin: 2 para separar del input de descuentos. -->
        <div class="tw:mt-8">
            <FormTerminos
                :error="errorTerminos"
                @continuar="onTerminosChange"
            />
        </div>

        <!-- Mensaje de gastos de gestión e impuestos, debajo de los checks. -->
        <p
            v-if="!precioInfo.price_management_interno && precioInfo.price_management > 0"
            class="tw:text-xs tw:text-gray-600 tw:mt-1 tw:mb-6"
        >
            * {{ t('general.el_precio', { precio: formatPrice(importeConDescuento), gastos: formatPrice(precioInfo.price_management), iva: 21 }) }}
        </p>

        <div class="tw:flex tw:justify-between tw:mb-6">
            <Button
                id="atras-paso-2"
                data-cy="atras-paso-2"
                type="button"
                :label="t('formularios.atras')"
                :disabled="deshabilitado"
                @click="$emit('atras')"
            />
            <Button
                id="continuar-pago"
                data-cy="continuar-pago"
                type="button"
                :label="t('formularios.continuar')"
                :disabled="deshabilitado || loading || !puedeAvanzar"
                @click="onContinuar"
            />
        </div>
    </section>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import store from '@/store';
    import CuponDescuentoComponent from '@/components/frontend/components/sga/CuponDescuentoComponent.vue';
    import FormTerminos from '@/components/frontend/components/FormTerminos.vue';
    import Button from '@/modules/ui/Button.vue';
    import { t } from '@/utils/i18n-helper';

    const props = defineProps({
        cartId: { type: [String, Number], default: null },
        precioInfo: { type: Object, required: true },
        descuentosAuto: { type: Array, default: () => [] },
        cuponVerificado: { type: Object, default: null },
        terminos: { type: Object, required: true },
        loading: { type: Boolean, default: false },
        deshabilitado: { type: Boolean, default: false },
        datosComprador: { type: Object, default: null },
        totalPagar: { type: Number, default: 0 },
        importeConDescuento: { type: Number, default: 0 },
        error: { type: String, default: null },
        applying: { type: Boolean, default: false },
    });

    const emit = defineEmits(['continuar', 'atras', 'cancelar', 'aplicar-cupon', 'quitar-cupon', 'verificar-cupon', 'condiciones-change']);

    const teatro = computed(() => store.getters.getTeatro || {});
    const textoSimpatizante = computed(() => teatro.value.texto_simpatizante || '');

    // Descuentos mostrados en el desglose: automáticos + cupón verificado.
    const descuentos = computed(() => {
        const list = (props.descuentosAuto || []).filter(Boolean);
        if (props.cuponVerificado) list.push(props.cuponVerificado);
        return list;
    });

    const errorTerminos = ref(false);

    const puedeAvanzar = computed(() => {
        const t = props.terminos || {};
        return !!(t.privacidad) && !!(t.condiciones);
    });

    function onTerminosChange(continuar, privacidad, condiciones, comunicaciones, plazos, renovacion) {
        emit('condiciones-change', { privacidad, condiciones, comunicaciones });
        if (privacidad && condiciones) errorTerminos.value = false;
    }

    function formatPrice(amount) {
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    }

    function formatDescuento(d) {
        const value = d?.value ?? d?.percentage ?? 0;
        const type = d?.valueType
            || (typeof d?.percentage === 'number' ? 'PERCENTAGE' : 'FIXED');
        return type === 'PERCENTAGE' ? `${value}%` : formatPrice(value);
    }

    function onContinuar() {
        if (!puedeAvanzar.value) {
            errorTerminos.value = true;
            return;
        }
        emit('continuar');
    }
</script>